import React from 'react';
import { Link } from '../Link';
import { SimpleSankey } from '../SimpleSankey';
import type { SankeyData } from '../../types/visualization';
import { useJobFlowSankeyFromRedux } from '../../hooks/useJobFlowSankeyFromRedux';
import { useAuth } from '../../hooks/useAuth';
import { TOOLS_BTN_PRIMARY } from '../../layouts/tools-page-classes';

export interface SrpActivitySankeyStripStrings {
  title: string;
  cta: string;
  teaser: string;
}

function SrpActivitySankeyStripLayout({
  locale,
  strings,
  scaledData,
  hasChart,
  isAuthenticated,
}: {
  locale: string;
  strings: SrpActivitySankeyStripStrings;
  scaledData: SankeyData | null;
  hasChart: boolean;
  /** Anonymous users with no activity yet already see the account sign-up CTA above this strip — skip the redundant empty-state teaser for them. */
  isAuthenticated: boolean;
}) {
  if (!hasChart && !isAuthenticated) {
    return null;
  }
  return (
    <section
      className="mt-3 mb-3 sm:mb-4 rounded-2xl border border-[#DDD5C8] dark:border-[#4A3520] bg-jc-surface shadow-sm dark:shadow-none overflow-hidden"
      aria-labelledby="srp-activity-strip-heading"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-4 py-3 border-b border-[#DDD5C8]/80 dark:border-[#4A3520]/80">
        <div className="min-w-0">
          <h2 id="srp-activity-strip-heading" className="text-base font-semibold text-jc-ink">
            {strings.title}
          </h2>
          {!hasChart ? <p className="text-sm text-jc-muted mt-1">{strings.teaser}</p> : null}
        </div>
        <Link
          href="/tools"
          locale={locale}
          className={`${TOOLS_BTN_PRIMARY} inline-flex items-center justify-center text-center whitespace-nowrap shrink-0`}
        >
          {strings.cta}
        </Link>
      </div>
      {hasChart && scaledData ? (
        <div className="h-[280px] w-full min-h-0 shrink-0 overflow-hidden bg-[#F8F5F0]/50 dark:bg-[#170F07]/40 px-2 sm:h-[320px] sm:px-3">
          <SimpleSankey
            data={scaledData}
            nodeWidth={12}
            nodePadding={40}
            smallData
            layoutEstimate={{ width: 360, height: 280 }}
          />
        </div>
      ) : null}
    </section>
  );
}

function SrpActivitySankeyStripRedux({
  locale,
  strings,
}: {
  locale: string;
  strings: SrpActivitySankeyStripStrings;
}) {
  const { scaledData, hasData } = useJobFlowSankeyFromRedux();
  const { isAuthenticated } = useAuth();
  return (
    <SrpActivitySankeyStripLayout
      locale={locale}
      strings={strings}
      scaledData={scaledData}
      hasChart={hasData}
      isAuthenticated={isAuthenticated}
    />
  );
}

/** Precomputed strip from SSR (static shell) — no Redux subscription. */
export function SrpActivitySankeyStripSsr(props: {
  viewMode: 'list' | 'map' | 'grid';
  locale: string;
  strings: SrpActivitySankeyStripStrings;
  scaledData: SankeyData | null;
  hasPositiveLink: boolean;
  isAuthenticated: boolean;
}) {
  if (props.viewMode === 'map') {
    return null;
  }
  return (
    <SrpActivitySankeyStripLayout
      locale={props.locale}
      strings={props.strings}
      scaledData={props.scaledData}
      hasChart={props.hasPositiveLink}
      isAuthenticated={props.isAuthenticated}
    />
  );
}

/** Live chart from Redux (interactive island, legacy search page). */
export function SrpActivitySankeyStripLive(props: {
  viewMode: 'list' | 'map' | 'grid';
  locale: string;
  strings: SrpActivitySankeyStripStrings;
}) {
  if (props.viewMode === 'map') {
    return null;
  }
  return <SrpActivitySankeyStripRedux locale={props.locale} strings={props.strings} />;
}
